import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { IncomeOptionsDTO } from '../../api/useIncomeQuery';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { getIncomeOwnerModel } from '../../Income.meta';

// Mirrors WhatIfIncomeChanges.OtherIncomeEndOfPlanEndAgeSentinel on the server.
const END_OF_PLAN_END_YEAR = -1;

interface OtherIncomeFormFieldsProps {
  formBag: any;
  options: IncomeOptionsDTO;
  fieldNamePrefix?: string;
  defaultShowAdvanced?: boolean;
}

export const OtherIncomeFormFields = ({
  formBag: { register, control, setValue, watch },
  fieldNamePrefix = '',
  options,
  defaultShowAdvanced = false,
}: OtherIncomeFormFieldsProps) => {
  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const earnerOptions = Object.keys(options.OwnerOptions).map(key => ({
    label: key,
    options: (options.OwnerOptions[key] || []).map(owner => ({
      value: owner.Name,
      label: owner.Name,
    })),
  }));

  const startYearOptions = [
    { value: null, label: 'Start Today' },
    ...options.StartYearOptions.map(year => ({
      value: year,
      label: year,
    })),
  ];

  const endYearOptions = [
    { value: END_OF_PLAN_END_YEAR, label: 'End of Plan' },
    { value: null, label: 'At Retirement' },
    ...options.OtherIncomeEndYearOptions.map(year => ({
      value: year,
      label: year,
    })),
  ];

  const managementStyleValue = watch(`${fieldNamePrefix}OrionManagementStyleId`);
  const endAtEndOfPlan = watch(`${fieldNamePrefix}EndAtEndOfPlan`);

  const matchesEndYearOption = (
    optionValue: number | null,
    selectedValue: number | null | undefined,
  ) => optionValue === selectedValue || (optionValue == null && selectedValue == null);

  // End of plan incomes are stored with a null EndAge, which would otherwise match "At Retirement".
  const toEndYearOptionValue = (endAge: number | null | undefined) =>
    endAtEndOfPlan ? END_OF_PLAN_END_YEAR : endAge;

  useEffect(() => {
    register(`${fieldNamePrefix}EndAtRetirement`);
    register(`${fieldNamePrefix}EndAtEndOfPlan`);
  }, [fieldNamePrefix, register]);

  return (
    <>
      <Input label="Name" {...register(`${fieldNamePrefix}Name`)} />

      <Controller
        name={`${fieldNamePrefix}Earner`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Earner"
            value={earnerOptions
              .map(({ options: ownerOptions }) => ownerOptions)
              .flat()
              .find(({ value: v }) => v === value)}
            options={earnerOptions}
            onChange={option => {
              onChange(option?.value);
              if (option) {
                setValue(
                  `${fieldNamePrefix}OwnerModel`,
                  getIncomeOwnerModel({ options: options.OwnerOptions, value: option.value }),
                );
              }
            }}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name={`${fieldNamePrefix}Amount`}
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Yearly Amount"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            tooltip="Please specify the amount before deducting taxes in today's dollars."
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName={`${fieldNamePrefix}OrionManagementStyleId`}
        onChange={(value: number | null) => {
          setValue(`${fieldNamePrefix}CustomBucketIndex`, value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName={`${fieldNamePrefix}CustomBucketIndex`}
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              name={`${fieldNamePrefix}StartAge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Start Year"
                  value={startYearOptions.find(({ value: v }) => v === value)}
                  options={startYearOptions}
                  onChange={option => onChange(option?.value)}
                  tooltip="Enter the year when the income will start. To start this income immediately, leave this field blank."
                  isPortal
                />
              )}
            />

            <Controller
              name={`${fieldNamePrefix}EndAge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="End Year"
                  value={endYearOptions.find(({ value: v }) =>
                    matchesEndYearOption(v, toEndYearOptionValue(value)),
                  )}
                  options={endYearOptions}
                  onChange={option => {
                    const selectedValue = option?.value ?? null;
                    onChange(selectedValue);
                    setValue(
                      `${fieldNamePrefix}EndAtEndOfPlan`,
                      selectedValue === END_OF_PLAN_END_YEAR,
                      {
                        shouldDirty: true,
                      },
                    );
                    setValue(`${fieldNamePrefix}EndAtRetirement`, selectedValue === null, {
                      shouldDirty: true,
                    });
                  }}
                  tooltip="Select the year when the income will stop. Choose At Retirement to stop it when the earner retires, or End of Plan to continue it through the end of the plan."
                  isPortal
                />
              )}
            />
          </Box>

          <Controller
            name={`${fieldNamePrefix}AccountEarmark`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                label="Asset Earmark"
                value={options.EarmarkOptions.filter(({ value: v }) =>
                  (value ?? []).some(({ Type }) => Type === v),
                )}
                options={options.EarmarkOptions}
                onChange={earmarks => onChange(earmarks.map(({ value: v }) => ({ Type: v })))}
                isMulti
                isPortal
              />
            )}
          />

          <Controller
            control={control}
            name={`${fieldNamePrefix}Inflation`}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Yearly Amount field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
              />
            )}
          />

          <Toggle
            {...register(`${fieldNamePrefix}IsTaxFree`)}
            size="small"
            helperText="If checked, this income will be free of federal and state taxes (e.g. Military combat pay). By default employment incomes are taxed."
          >
            Tax Free
          </Toggle>
        </>
      )}
    </>
  );
};
